import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  BookOpen, Brain, Trophy, Flame, 
  ArrowRight, CheckCircle, GraduationCap, Dumbbell
} from 'lucide-react';
import CabinCrewLayout from '../layouts/CabinCrewLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Exercises = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [categories, setCategories] = useState(null);
  const [progress, setProgress] = useState(null);
  const [streak, setStreak] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // URL'den tab parametresini al, yoksa 'tenses' kullan
  const tabFromUrl = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(tabFromUrl || 'tenses');
  
  // URL değiştiğinde tab'ı güncelle
  useEffect(() => {
    if (tabFromUrl && ['tenses', 'basic', 'practice', 'vocabulary'].includes(tabFromUrl)) {
      setActiveTab(tabFromUrl);
    }
  }, [tabFromUrl]);
  
  useEffect(() => {
    loadData();
  }, []);
  
  const loadData = async () => {
    try {
      const [catRes, progRes, streakRes] = await Promise.all([
        axios.get(`${API}/exercises/categories`),
        axios.get(`${API}/exercises/progress`, { withCredentials: true }).catch(() => ({ data: { user_id: '', total_completed: 0, grammar_a1: {}, grammar_a2: {}, vocabulary_a1: {}, vocabulary_a2: {} } })),
        axios.get(`${API}/exercises/streak`, { withCredentials: true }).catch(() => ({ data: { current_streak: 0, longest_streak: 0 } }))
      ]);
      
      setCategories(catRes.data);
      setProgress(progRes.data);
      setStreak(streakRes.data);
    } catch (error) {
      toast.error('Veri yüklenemedi');
    } finally {
      setLoading(false);
    }
  };
  
  const startExercise = (category, level, topicOrTheme) => {
    if (category === 'grammar') {
      navigate(`/exercises/grammar/${level}/${topicOrTheme}`);
    } else {
      navigate(`/exercises/vocabulary/${level}/${topicOrTheme}`);
    }
  };
  
  const goToLesson = (level, topic) => {
    navigate(`/grammar-lesson/${level}/${topic}`);
  };
  
  const getProgressForTopic = (category, level, topic) => {
    if (!progress) return null;
    const key = `${category}_${level.toLowerCase()}`;
    return progress[key]?.[topic];
  };
  
  const calculateCategoryProgress = (category, level) => {
    if (!progress || !categories) return 0;
    const key = `${category}_${level.toLowerCase()}`;
    const topics = category === 'grammar' ? 
      Object.keys(categories.grammar[level] || {}) : 
      Object.keys(categories.vocabulary[level] || {});
    
    const completed = topics.filter(topic => progress[key]?.[topic]?.completed).length;
    return topics.length > 0 ? (completed / topics.length) * 100 : 0;
  };
  
  if (loading) {
    return (
      <CabinCrewLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Yükleniyor...</p>
          </div>
        </div>
      </CabinCrewLayout>
    );
  }
  
  // Zamanlar listesi - bunları Diğer Gramer Konuları'ndan çıkar
  const TENSE_TOPICS = ['present_simple', 'present_continuous', 'past_simple', 'past_continuous', 
    'present_perfect', 'past_perfect', 'future_forms', 'future_simple', 'future_going_to'];
  
  // Render topic cards for lessons (Gramer Öğren) - ZAMANLAR HARİÇ
  const renderLessonCards = (level, colorClass, borderColor) => {
    const topics = categories?.grammar?.[level];
    if (!topics) return null;
    
    // Zaman konularını filtrele
    const filteredTopics = Object.entries(topics).filter(([key]) => !TENSE_TOPICS.includes(key));
    
    return filteredTopics.map(([key, topic]) => (
      <Card key={key} className={`hover:shadow-lg transition-all cursor-pointer ${borderColor} hover:scale-[1.02]`}>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <GraduationCap className={`w-5 h-5 ${colorClass}`} />
            <span>{topic.name}</span>
          </CardTitle>
          <p className="text-sm text-gray-600">{topic.description}</p>
        </CardHeader>
        <CardContent>
          <Button
            className={`w-full bg-gradient-to-r ${level === 'A1' ? 'from-blue-600 to-blue-700' : 'from-indigo-600 to-indigo-700'}`}
            onClick={() => navigate(`/grammar-lesson/${level}/${key}`)}
          >
            <BookOpen className="w-4 h-4 mr-2" />
            Dersi Başlat
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </CardContent>
      </Card>
    ));
  };
  
  // Render topic cards for practice (Gramer Pratik)
  const renderPracticeCards = (level, colorClass, borderColor, gradientFrom, gradientTo) => {
    const topics = categories?.grammar?.[level];
    if (!topics) return null;
    
    return Object.entries(topics).map(([key, topic]) => {
      const topicProgress = getProgressForTopic('grammar', level, key);
      
      return (
        <Card key={key} className={`hover:shadow-lg transition-shadow ${borderColor}`}>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Dumbbell className={`w-5 h-5 ${colorClass}`} />
                <span>{topic.name}</span>
              </div>
              {topicProgress?.completed && (
                <CheckCircle className="w-5 h-5 text-green-600" />
              )}
            </CardTitle>
            <p className="text-sm text-gray-600">{topic.description}</p>
            {topicProgress?.completed && (
              <Badge className="bg-green-100 text-green-800 mt-2 w-fit">
                {topicProgress.score.toFixed(0)}% puan
              </Badge>
            )}
          </CardHeader>
          <CardContent>
            <Button
              className={`w-full bg-gradient-to-r ${gradientFrom} ${gradientTo}`}
              onClick={() => startExercise('grammar', level, key)}
            >
              {topicProgress?.completed ? 'Tekrar Yap' : 'Pratik Yap'}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </CardContent>
        </Card>
      );
    });
  };
  
  // Render vocabulary cards (Kelime Egzersizleri)
  const renderVocabularyCards = (level, colorClass, borderColor, gradientFrom, gradientTo) => {
    const themes = categories?.vocabulary?.[level];
    if (!themes) return null;
    
    return Object.entries(themes).map(([key, theme]) => {
      const themeProgress = getProgressForTopic('vocabulary', level, key);
      
      return (
        <Card key={key} className={`hover:shadow-lg transition-shadow ${borderColor}`}>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center justify-between">
              <span>{theme.name}</span>
              {themeProgress?.completed && (
                <CheckCircle className="w-5 h-5 text-green-600" />
              )}
            </CardTitle>
            <p className="text-sm text-gray-600">{theme.description}</p>
            {themeProgress?.completed && (
              <Badge className="bg-green-100 text-green-800 mt-2 w-fit">
                {themeProgress.score.toFixed(0)}% puan
              </Badge>
            )}
          </CardHeader>
          <CardContent>
            <Button
              className={`w-full bg-gradient-to-r ${gradientFrom} ${gradientTo}`}
              onClick={() => startExercise('vocabulary', level, key)}
            >
              {themeProgress?.completed ? 'Tekrar Yap' : 'Başla'}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </CardContent>
        </Card>
      );
    });
  };
  
  return (
    <CabinCrewLayout>
      <div className="min-h-screen py-8">
        <div className="container mx-auto px-4 max-w-7xl">
          {/* Header */}
          <div className="mb-8">
            <Button
              variant="outline"
              onClick={() => navigate('/dashboard')}
              className="mb-4"
            >
              ← Panele Dön
            </Button>
            <h1 className="text-3xl sm:text-4xl font-bold gradient-text mb-2">📚 İngilizce Gramer Eğitimi</h1>
            <p className="text-gray-600">Önce öğren, sonra pratik yap!</p>
          </div>
          
          {/* Stats Row */}
          <div className="grid grid-cols-2 md:grid-cols-2 gap-4 mb-8">
            {/* Streak */}
            <Card className="border-orange-200 bg-gradient-to-br from-orange-50 to-white">
              <CardContent className="p-4 sm:p-6">
                <div className="flex items-center gap-3">
                  <Flame className="w-8 h-8 text-orange-500" />
                  <div>
                    <p className="text-xs sm:text-sm text-gray-600">Günlük Seri</p>
                    <p className="text-2xl sm:text-3xl font-bold text-orange-600">
                      {streak?.current_streak || 0}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Total Completed */}
            <Card className="border-green-200 bg-gradient-to-br from-green-50 to-white">
              <CardContent className="p-4 sm:p-6">
                <div className="flex items-center gap-3">
                  <Trophy className="w-8 h-8 text-green-500" />
                  <div>
                    <p className="text-xs sm:text-sm text-gray-600">Tamamlanan</p>
                    <p className="text-2xl sm:text-3xl font-bold text-green-600">
                      {progress?.total_completed || 0}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* Main Content - 4 Tabs */}
          <Card className="glass-effect border-none shadow-xl">
            <CardContent className="p-4 sm:p-6">
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="grid w-full grid-cols-4 mb-6">
                  <TabsTrigger value="tenses" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2">
                    <span className="hidden sm:inline">⏰</span>
                    <span className="truncate">Zamanlar</span>
                  </TabsTrigger>
                  <TabsTrigger value="basic" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2">
                    <GraduationCap className="w-4 h-4 hidden sm:block" />
                    <span className="truncate">Temel Gramer</span>
                  </TabsTrigger>
                  <TabsTrigger value="practice" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2">
                    <Dumbbell className="w-4 h-4 hidden sm:block" />
                    <span className="truncate">Pratik</span>
                  </TabsTrigger>
                  <TabsTrigger value="vocabulary" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2">
                    <BookOpen className="w-4 h-4 hidden sm:block" />
                    <span className="truncate">Kelime</span>
                  </TabsTrigger>
                </TabsList>
                
                {/* Tab 1: Zamanlar Eğitimi (English Tenses) */}
                <TabsContent value="tenses">
                  <div className="space-y-6">
                    {/* Header */}
                    <div className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-xl p-6">
                      <div className="flex items-center gap-4">
                        <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
                          <span className="text-3xl">⏰</span>
                        </div>
                        <div>
                          <h2 className="text-2xl sm:text-3xl font-bold mb-1">Zamanlar Eğitimi</h2>
                          <p className="text-white/80 text-sm sm:text-base">English Tenses - Tüm İngilizce zamanları öğren</p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Tenses Grid */}
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {/* Present Simple */}
                      <Card className="hover:shadow-lg transition-all cursor-pointer border-blue-200 hover:scale-[1.02]">
                        <CardHeader className="pb-2">
                          <CardTitle className="text-lg flex items-center gap-2">
                            <span className="text-2xl">🔵</span>
                            <span>Present Simple</span>
                          </CardTitle>
                          <p className="text-sm text-gray-600">Geniş zaman - I work, She works</p>
                        </CardHeader>
                        <CardContent>
                          <Button
                            className="w-full bg-gradient-to-r from-blue-600 to-blue-700"
                            onClick={() => navigate('/grammar-lesson/A1/present_simple')}
                          >
                            <BookOpen className="w-4 h-4 mr-2" />
                            Dersi Başlat
                            <ArrowRight className="w-4 h-4 ml-2" />
                          </Button>
                        </CardContent>
                      </Card>
                      
                      {/* Present Continuous */}
                      <Card className="hover:shadow-lg transition-all cursor-pointer border-blue-200 hover:scale-[1.02]">
                        <CardHeader className="pb-2">
                          <CardTitle className="text-lg flex items-center gap-2">
                            <span className="text-2xl">🔵</span>
                            <span>Present Continuous</span>
                          </CardTitle>
                          <p className="text-sm text-gray-600">Şimdiki zaman - I am working</p>
                        </CardHeader>
                        <CardContent>
                          <Button
                            className="w-full bg-gradient-to-r from-blue-600 to-blue-700"
                            onClick={() => navigate('/grammar-lesson/A1/present_continuous')}
                          >
                            <BookOpen className="w-4 h-4 mr-2" />
                            Dersi Başlat
                            <ArrowRight className="w-4 h-4 ml-2" />
                          </Button>
                        </CardContent>
                      </Card>
                      
                      {/* Past Simple */}
                      <Card className="hover:shadow-lg transition-all cursor-pointer border-orange-200 hover:scale-[1.02]">
                        <CardHeader className="pb-2">
                          <CardTitle className="text-lg flex items-center gap-2">
                            <span className="text-2xl">🟠</span>
                            <span>Past Simple</span>
                          </CardTitle>
                          <p className="text-sm text-gray-600">Geçmiş zaman - I worked, She went</p>
                        </CardHeader>
                        <CardContent>
                          <Button
                            className="w-full bg-gradient-to-r from-orange-600 to-orange-700"
                            onClick={() => navigate('/grammar-lesson/A2/past_simple')}
                          >
                            <BookOpen className="w-4 h-4 mr-2" />
                            Dersi Başlat
                            <ArrowRight className="w-4 h-4 ml-2" />
                          </Button>
                        </CardContent>
                      </Card>
                      
                      {/* Past Continuous */}
                      <Card className="hover:shadow-lg transition-all cursor-pointer border-orange-200 hover:scale-[1.02]">
                        <CardHeader className="pb-2">
                          <CardTitle className="text-lg flex items-center gap-2">
                            <span className="text-2xl">🟠</span>
                            <span>Past Continuous</span>
                          </CardTitle>
                          <p className="text-sm text-gray-600">Geçmişte sürekli - I was working</p>
                        </CardHeader>
                        <CardContent>
                          <Button
                            className="w-full bg-gradient-to-r from-orange-600 to-orange-700"
                            onClick={() => navigate('/grammar-lesson/A2/past_continuous')}
                          >
                            <BookOpen className="w-4 h-4 mr-2" />
                            Dersi Başlat
                            <ArrowRight className="w-4 h-4 ml-2" />
                          </Button>
                        </CardContent>
                      </Card>
                      
                      {/* Present Perfect */}
                      <Card className="hover:shadow-lg transition-all cursor-pointer border-green-200 hover:scale-[1.02]">
                        <CardHeader className="pb-2">
                          <CardTitle className="text-lg flex items-center gap-2">
                            <span className="text-2xl">🟢</span>
                            <span>Present Perfect</span>
                          </CardTitle>
                          <p className="text-sm text-gray-600">Yakın geçmiş - I have worked</p>
                        </CardHeader>
                        <CardContent>
                          <Button
                            className="w-full bg-gradient-to-r from-green-600 to-green-700"
                            onClick={() => navigate('/grammar-lesson/A2/present_perfect')}
                          >
                            <BookOpen className="w-4 h-4 mr-2" />
                            Dersi Başlat
                            <ArrowRight className="w-4 h-4 ml-2" />
                          </Button>
                        </CardContent>
                      </Card>
                      
                      {/* Future Simple */}
                      <Card className="hover:shadow-lg transition-all cursor-pointer border-purple-200 hover:scale-[1.02]">
                        <CardHeader className="pb-2">
                          <CardTitle className="text-lg flex items-center gap-2">
                            <span className="text-2xl">🟣</span>
                            <span>Future Simple</span>
                          </CardTitle>
                          <p className="text-sm text-gray-600">Gelecek zaman - I will work</p>
                        </CardHeader>
                        <CardContent>
                          <Button
                            className="w-full bg-gradient-to-r from-purple-600 to-purple-700"
                            onClick={() => navigate('/grammar-lesson/A2/future_simple')}
                          >
                            <BookOpen className="w-4 h-4 mr-2" />
                            Dersi Başlat
                            <ArrowRight className="w-4 h-4 ml-2" />
                          </Button>
                        </CardContent>
                      </Card>
                    </div>
                    
                    {/* Tüm zamanlar link */}
                    <div className="text-center pt-4">
                      <Button
                        variant="outline"
                        className="border-indigo-300 text-indigo-600 hover:bg-indigo-50"
                        onClick={() => navigate('/tenses')}
                      >
                        Tüm Zamanları Gör (12 Tense)
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </div>
                  </div>
                </TabsContent>
                
                {/* Tab 2: Basic English Grammar (Temel Gramer) */}
                <TabsContent value="basic">
                  <div className="space-y-6">
                    {/* Header */}
                    <div className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-xl p-6">
                      <div className="flex items-center gap-4">
                        <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
                          <GraduationCap className="w-10 h-10" />
                        </div>
                        <div>
                          <h2 className="text-2xl sm:text-3xl font-bold mb-1">Temel İngilizce Gramer</h2>
                          <p className="text-white/80 text-sm sm:text-base">Basic English Grammar - Zamanlar dışındaki temel konular</p>
                        </div>
                      </div>
                    </div>
                    
                    {/* A1 Level */}
                    <div>
                      <h3 className="text-xl font-bold text-blue-600 flex items-center gap-2 mb-4">
                        <Badge className="bg-blue-100 text-blue-700">A1</Badge>
                        Başlangıç Seviyesi
                      </h3>
                      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {renderLessonCards('A1', 'text-blue-600', 'border-blue-200')}
                      </div>
                    </div>
                    
                    {/* A2 Level */}
                    <div>
                      <h3 className="text-xl font-bold text-indigo-600 flex items-center gap-2 mb-4">
                        <Badge className="bg-indigo-100 text-indigo-700">A2</Badge>
                        Temel Seviye
                      </h3>
                      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {renderLessonCards('A2', 'text-indigo-600', 'border-indigo-200')}
                      </div>
                    </div>
                  </div>
                </TabsContent>
                
                {/* Tab 3: Gramer Pratik (Grammar Practice) */}
                <TabsContent value="practice">
                  <div className="space-y-8">
                    {/* Header */}
                    <div className="bg-gradient-to-r from-amber-500 to-orange-600 text-white rounded-xl p-6">
                      <div className="flex items-center gap-4">
                        <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
                          <Dumbbell className="w-10 h-10" />
                        </div>
                        <div>
                          <h2 className="text-2xl sm:text-3xl font-bold mb-1">Gramer Pratik</h2>
                          <p className="text-white/80 text-sm sm:text-base">Grammar Practice - Öğrendiğin konuları test et!</p>
                        </div>
                      </div>
                    </div>
                    
                    {/* A1 Level */}
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-xl sm:text-2xl font-bold text-blue-600 flex items-center gap-2">
                          <Badge className="bg-blue-100 text-blue-700">A1</Badge>
                          Başlangıç
                        </h3>
                        <div className="flex items-center gap-2">
                          <Progress value={calculateCategoryProgress('grammar', 'A1')} className="w-24 sm:w-32" />
                          <span className="text-sm font-semibold text-blue-600">
                            {calculateCategoryProgress('grammar', 'A1').toFixed(0)}%
                          </span>
                        </div>
                      </div>
                      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {renderPracticeCards('A1', 'text-blue-600', 'border-blue-200', 'from-blue-600', 'to-blue-700')}
                      </div>
                    </div>
                    
                    {/* A2 Level */}
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-xl sm:text-2xl font-bold text-indigo-600 flex items-center gap-2">
                          <Badge className="bg-indigo-100 text-indigo-700">A2</Badge>
                          Temel
                        </h3>
                        <div className="flex items-center gap-2">
                          <Progress value={calculateCategoryProgress('grammar', 'A2')} className="w-24 sm:w-32" />
                          <span className="text-sm font-semibold text-indigo-600">
                            {calculateCategoryProgress('grammar', 'A2').toFixed(0)}%
                          </span>
                        </div>
                      </div>
                      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {renderPracticeCards('A2', 'text-indigo-600', 'border-indigo-200', 'from-indigo-600', 'to-indigo-700')}
                      </div>
                    </div>
                  </div>
                </TabsContent>
                
                {/* Tab 4: Kelime Egzersizleri (Vocabulary Exercises) */}
                <TabsContent value="vocabulary">
                  <div className="space-y-8">
                    {/* Header */}
                    <div className="bg-gradient-to-r from-purple-500 to-pink-600 text-white rounded-xl p-6">
                      <div className="flex items-center gap-4">
                        <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
                          <BookOpen className="w-10 h-10" />
                        </div>
                        <div>
                          <h2 className="text-2xl sm:text-3xl font-bold mb-1">Kelime Egzersizleri</h2>
                          <p className="text-white/80 text-sm sm:text-base">Vocabulary Exercises - Kelime hazineni genişlet!</p>
                        </div>
                      </div>
                    </div>
                    
                    {/* A1 Level */}
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-xl sm:text-2xl font-bold text-purple-600 flex items-center gap-2">
                          <Badge className="bg-purple-100 text-purple-700">A1</Badge>
                          Başlangıç
                        </h3>
                        <div className="flex items-center gap-2">
                          <Progress value={calculateCategoryProgress('vocabulary', 'A1')} className="w-24 sm:w-32" />
                          <span className="text-sm font-semibold text-purple-600">
                            {calculateCategoryProgress('vocabulary', 'A1').toFixed(0)}%
                          </span>
                        </div>
                      </div>
                      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {renderVocabularyCards('A1', 'text-purple-600', 'border-purple-200', 'from-purple-600', 'to-purple-700')}
                      </div>
                    </div>
                    
                    {/* A2 Level */}
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-xl sm:text-2xl font-bold text-pink-600 flex items-center gap-2">
                          <Badge className="bg-pink-100 text-pink-700">A2</Badge>
                          Temel
                        </h3>
                        <div className="flex items-center gap-2">
                          <Progress value={calculateCategoryProgress('vocabulary', 'A2')} className="w-24 sm:w-32" />
                          <span className="text-sm font-semibold text-pink-600">
                            {calculateCategoryProgress('vocabulary', 'A2').toFixed(0)}%
                          </span>
                        </div>
                      </div>
                      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {renderVocabularyCards('A2', 'text-pink-600', 'border-pink-200', 'from-pink-600', 'to-pink-700')}
                      </div>
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>
      </div>
    </CabinCrewLayout>
  );
};

export default Exercises;
